import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Col, Empty, Space, Typography } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import pmvService from '@/services/pmv.service';

const { Text } = Typography;

const PmvReliabilityControlTowerPage: React.FC = () => {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['pmv-reliability-control-tower'],
    queryFn: () => pmvService.getReliabilityControlTower(),
    staleTime: 30_000,
  });
  const d = q.data;
  const openAttn = d?.open_attention ?? d?.attention_requiring_action ?? 0;
  const fleet = d?.fleet_health || d?.health_by_state || {};

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Reliability Control Tower"
        subtitle="Health, failures, and predictions — OBSERVE / RECOMMEND only"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/reliability')}>Health Register</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/attention')}>Attention</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/failures')}>Failures</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/forecast')}>Forecast</Button>
            <Button onClick={() => navigate('/asset/pmv/control-tower')}>Fleet Command Center</Button>
          </Space>
        }
      />

      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="Authority boundary"
        description={
          d?.note ||
          'Reliability observes and recommends. Maintenance owns cases. PMV operational status is never auto-changed. UNKNOWN health is shown honestly.'
        }
      />

      {!q.isLoading && openAttn === 0 && (d?.critical_health ?? 0) === 0 ? (
        <Empty style={{ marginBottom: 16 }} description="No reliability exceptions requiring action." />
      ) : null}

      <ControlTowerSectionCard zone="attention" title="Requires attention">
        <ControlTowerMetricStrip zone="attention" title="Attention">
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Open reliability attention"
              count={openAttn}
              urgency={openAttn > 0 ? 'attention' : 'neutral'}
              footerLink={{
                label: 'Open queue',
                onClick: () => navigate('/asset/pmv/reliability/attention'),
              }}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Critical health"
              count={d?.critical_health ?? 0}
              urgency={(d?.critical_health ?? 0) > 0 ? 'attention' : 'neutral'}
              footerLink={{
                label: 'Critical register',
                onClick: () => navigate('/asset/pmv/reliability?health_state=CRITICAL'),
              }}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Unknown health"
              count={d?.unknown_health ?? 0}
              footerLink={{
                label: 'Unknown register',
                onClick: () => navigate('/asset/pmv/reliability?health_state=UNKNOWN'),
              }}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Active predictions"
              count={d?.active_predictions ?? 0}
              footerLink={{
                label: 'Predictions',
                onClick: () => navigate('/asset/pmv/reliability/predictions'),
              }}
            />
          </Col>
        </ControlTowerMetricStrip>
      </ControlTowerSectionCard>

      <ControlTowerSectionCard zone="metricsOrders" title="Fleet health distribution">
        <ControlTowerMetricStrip zone="metricsOrders" title="Health">
          {['CRITICAL', 'POOR', 'DEGRADED', 'WATCH', 'HEALTHY', 'UNKNOWN'].map((state) => (
            <Col xs={12} sm={8} md={4} key={state}>
              <ControlTowerMetricTile
                title={state}
                count={Number(fleet[state] || 0)}
                urgency={state === 'CRITICAL' || state === 'POOR' ? 'attention' : 'neutral'}
              />
            </Col>
          ))}
        </ControlTowerMetricStrip>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Missing evidence stays UNKNOWN — never shown as HEALTHY 100.
        </Text>
      </ControlTowerSectionCard>
    </WorkspaceLayout>
  );
};

export default PmvReliabilityControlTowerPage;
